
// Padding/line-height fixes for the bubble theme, which has no vertical padding of its own.
// Imported here rather than from each module's stylesheet so every app that registers these
// toasts gets the styling with them.
import './Toast.css'

// Reading-time rule of thumb: give a toast about a second per 13 characters, floored so a
// short message is still readable and capped so a long one does not camp on the screen.
// 13 char/s is the low end of average prose reading speed, which is the right end to aim at
// for something you did not choose to read and that appears in the corner of your eye.
// Material's snackbar guidance (4-10s) is the same ballpark for short text; the floor and cap
// here are deliberately a little wider because these carry API error text of unknown length.
//
// Only errors are timed this way. Success and info are acknowledgements -- nobody needs to
// dwell on "saved" -- so they keep their short fixed durations.
// The floor is well above what the reading-speed maths alone would give (a 74-char message
// works out to ~5.7s). That is deliberate: the rule assumes reading starts the moment the
// text appears, but a toast shows up in your peripheral vision while you are looking at the
// form you just submitted, so it needs time to be *noticed* before it can be read.
const MS_PER_CHAR = 1000 / 13
const MIN_DURATION = 10000
const MAX_DURATION = 20000

export const readingDuration = (text) => {
  const length = typeof text === 'string' ? text.length : 0
  return Math.min(MAX_DURATION, Math.max(MIN_DURATION, Math.round(length * MS_PER_CHAR)))
}

// Shared by the registered toast and the global.error override below, so both render the same
// text: a plain string passes through, anything else (an API error object, an array of
// validation messages) gets stringified rather than showing "[object Object]".
const errorText = (payload) => {
  const message = payload && payload.message
  return typeof message === 'string' ? message : JSON.stringify(message)
}

// NB: vue-toasted reads `className`, not `class` -- these hooks were previously written as
// `class` and so were never applied to the toast element at all.
const errorOptions = {
  position: 'top-center',
  type: 'error',
  theme: 'bubble',
  iconPack: 'fontawesome',
  icon: {
    name: 'exclamation-circle',
    after: true
  },
  className: 'sw__toast__error',
}

export default (Vue) => {
  // Registered with a static duration as well, because `register` passes its options object
  // straight through to the toast and cannot vary per message. The override at the bottom is
  // what actually computes the duration; this value is the fallback if that override is ever
  // lost (vue-toasted rebuilds `global` on every register call).
  Vue.toasted.register('error', errorText, { ...errorOptions, duration: 8000 })

  Vue.toasted.register('success', msg => msg.message, {
    position: 'top-center',
    type: 'success',
    theme: 'bubble',
    iconPack: 'fontawesome',
    icon: {
      name: 'check',
      after: true
    },
    className: 'sw__toast__success',
    duration: 5000
  })

  Vue.toasted.register('info', msg => msg.message, {
    position: 'top-center',
    type: 'info',
    theme: 'bubble',
    iconPack: 'fontawesome',
    icon: {
      name: 'info-circle',
      after: true
    },
    className: 'sw__toast__info',
    duration: 4000
  })

  Vue.toasted.register('warning', msg => msg.message, {
    position: 'top-center',
    type: 'warning',
    theme: 'bubble',
    iconPack: 'fontawesome',
    icon: {
      name: 'check',
      after: true
    },
    className: 'sw__toast__warning',
    duration: 5000
  })

  // Length-aware duration for errors. Has to come after every register() above: vue-toasted
  // resets `toasted.global` to {} each time one runs, which would drop this. Overriding
  // `global.error` rather than changing call sites keeps every existing
  // `this.$toasted.global.error({ message })` working untouched.
  Vue.toasted.global.error = (payload = {}) => {
    const text = errorText(payload)
    return Vue.toasted.show(text, { ...errorOptions, duration: readingDuration(text) })
  }
}
